import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { Translate as TranslateIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getCountryFlag } from '../../utils/countryFlags';
import { schemaApiService } from '../../services/schemaApiService';

interface TranslationDialogProps {
  open: boolean;
  onClose: () => void;
  schema: any;
  onSuccess?: (message: string) => void;
}

const TranslationDialog: React.FC<TranslationDialogProps> = ({
  open,
  onClose,
  schema,
  onSuccess
}) => {
  const websites = useSelector((state: RootState) => state.website.websites);
  const [selectedWebsites, setSelectedWebsites] = useState<string[]>([]);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group websites by unique countries/languages
  const uniqueWebsites = websites.reduce((acc: any[], website) => {
    const existing = acc.find(w => w.country === website.country);
    if (!existing) {
      acc.push(website);
    }
    return acc;
  }, []);

  const handleToggleWebsite = (websiteId: string) => {
    setSelectedWebsites(prev => {
      if (prev.includes(websiteId)) {
        return prev.filter(id => id !== websiteId);
      }
      return [...prev, websiteId];
    });
  };

  const handleSelectAll = () => {
    if (selectedWebsites.length === uniqueWebsites.length) {
      setSelectedWebsites([]);
    } else {
      setSelectedWebsites(uniqueWebsites.map(w => w.website_id));
    }
  };

  const handleTranslate = async () => {
    try {
      setTranslating(true);
      setError(null);

      // Call the translation API
      const result = await schemaApiService.translateSchema(
        schema.id,
        selectedWebsites
      );

      if (result.success) {
        onSuccess?.(
          `🌍 Successfully queued translation to ${selectedWebsites.length} languages! This will take a few minutes.`
        );
        onClose();
      } else {
        setError(result.error || 'Failed to translate schema');
      }
    } catch (err: any) {
      setError(err.message || 'Error translating schema');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TranslateIcon color="primary" />
        Translate Schema to Other Languages
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Select the target languages/websites for translation:
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Schema:
          </Typography>
          <Chip 
            label={schema?.url || 'Unknown URL'}
            size="small"
            sx={{ mb: 2 }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            variant="outlined"
          >
            {selectedWebsites.length === uniqueWebsites.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        <FormGroup>
          {uniqueWebsites.map(website => (
            <FormControlLabel
              key={website.website_id}
              control={
                <Checkbox
                  checked={selectedWebsites.includes(website.website_id)}
                  onChange={() => handleToggleWebsite(website.website_id)}
                  disabled={translating}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{getCountryFlag(website.country)}</span>
                  <Typography variant="body2">
                    {website.country} - {website.base_url}
                  </Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>

        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> The schema will be translated and adapted for each selected market. 
              URLs and local information will be automatically adjusted.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={translating}>
          Cancel
        </Button>
        <Button
          onClick={handleTranslate}
          variant="contained"
          disabled={translating || selectedWebsites.length === 0}
          startIcon={translating ? <CircularProgress size={20} /> : <TranslateIcon />}
        >
          {translating ? 'Translating...' : `Translate to ${selectedWebsites.length} Languages`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslationDialog;